import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { portalService } from '../../services/api';

interface AcknowledgementData {
    id: string;
    tenantName: string;
    propertyAddress: string;
    landlordName: string;
    documents: string[];
    status: 'PENDING' | 'ACKNOWLEDGED' | 'EXPIRED';
    createdAt: string;
    acknowledgedAt?: string;
}

export default function TenantPortal() {
    const { token } = useParams<{ token: string }>();
    const [data, setData] = useState<AcknowledgementData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [confirmedDocs, setConfirmedDocs] = useState<string[]>([]);
    const [signature, setSignature] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            if (!token) {
                setError("Invalid link.");
                setLoading(false);
                return;
            }
            try {
                const res = await portalService.getAcknowledgement(token);
                if (res.data.success) {
                    setData(res.data.data);
                } else {
                    setError("Request not found or expired.");
                }
            } catch (err: any) {
                setError(err.response?.data?.message || "Failed to load request.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [token]);

    const handleConfirm = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!data || !token) return;

        // Allow partial acknowledgement — the tenant ticks what they actually
        // received. Some listed docs may be "reference-only" (no file
        // attached because the landlord uploaded metadata only), in which
        // case the tenant honestly can't acknowledge receipt of a file they
        // never got. The footer text says "I acknowledge receipt of the
        // SELECTED documents", so partial confirmation is consistent.
        if (confirmedDocs.length === 0) {
            alert("Please tick at least one document you have received.");
            return;
        }

        setSubmitting(true);
        try {
            await portalService.confirmAcknowledgement(token, {
                confirmedDocuments: confirmedDocs,
                signature,
                // Submitting the form is the consent action. Backend
                // requires this boolean and 400s without it.
                consent: true
            });
            setSuccess(true);
        } catch (err: any) {
            alert(err.response?.data?.message || "Failed to submit acknowledgement.");
        } finally {
            setSubmitting(false);
        }
    };

    const toggleDoc = (doc: string) => {
        if (data?.status !== 'PENDING') return;
        setConfirmedDocs(prev =>
            prev.includes(doc) ? prev.filter(d => d !== doc) : [...prev, doc]
        );
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50">Loading...</div>;

    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
                <div className="text-red-500 text-5xl mb-4">⚠️</div>
                <h1 className="text-xl font-bold text-gray-900 mb-2">Unavailable</h1>
                <p className="text-gray-600">{error}</p>
            </div>
        </div>
    );

    if (success || data?.status === 'ACKNOWLEDGED') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-green-50 p-4">
                <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full text-center border-t-4 border-green-500">
                    <div className="text-green-500 text-6xl mb-4">✅</div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
                    <p className="text-gray-600 mb-6">
                        You have successfully acknowledged receipt of the compliance documents.
                    </p>
                    <div className="bg-gray-50 rounded p-4 text-left text-sm text-gray-500">
                        <p><strong>Property:</strong> {data?.propertyAddress}</p>
                        <p><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
                        <p>A confirmation email has been sent to you.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8 font-sans">
            <div className="max-w-xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-indigo-600 px-6 py-4">
                    <h2 className="text-xl font-bold text-white flex items-center">
                        <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Document Acknowledgement
                    </h2>
                </div>

                <div className="p-8">
                    <div className="mb-6">
                        <p className="text-gray-500 text-sm uppercase tracking-wide font-semibold">Tenant</p>
                        <p className="text-lg font-medium text-gray-900">{data?.tenantName}</p>
                    </div>

                    <div className="mb-6">
                        <p className="text-gray-500 text-sm uppercase tracking-wide font-semibold">Property</p>
                        <p className="text-lg font-medium text-gray-900">{data?.propertyAddress}</p>
                    </div>

                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
                        <div className="flex">
                            <div className="ml-3">
                                <p className="text-sm text-blue-700">
                                    Please confirm that you have received the following documents from your landlord, <strong>{data?.landlordName}</strong>.
                                </p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleConfirm} className="space-y-6">
                        <div className="space-y-4">
                            {data?.documents.map((doc) => (
                                <div key={doc} className="relative flex items-start p-3 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => toggleDoc(doc)}>
                                    <div className="flex items-center h-5">
                                        <input
                                            id={`doc-${doc}`}
                                            type="checkbox"
                                            className="focus:ring-indigo-500 h-5 w-5 text-indigo-600 border-gray-300 rounded"
                                            checked={confirmedDocs.includes(doc)}
                                            readOnly
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor={`doc-${doc}`} className="font-medium text-gray-700 cursor-pointer">
                                            I have received the <span className="font-bold text-indigo-600">{doc.toUpperCase().replace('_', ' ')}</span>
                                        </label>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="border-t border-gray-200 pt-6 mt-6">
                            <label htmlFor="signature" className="block text-sm font-medium text-gray-700 mb-2">
                                Digital Signature (Type your full name)
                            </label>
                            <input
                                type="text"
                                id="signature"
                                required
                                placeholder={data?.tenantName}
                                className="block w-full border border-gray-300 rounded-md shadow-sm py-3 px-4 focus:ring-indigo-500 focus:border-indigo-500"
                                value={signature}
                                onChange={(e) => setSignature(e.target.value)}
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                By clicking "Confirm Receipt", I legally acknowledge receipt of the selected documents.
                            </p>
                        </div>

                        <button
                            type="submit"
                            disabled={submitting || confirmedDocs.length === 0 || !signature}
                            className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${(submitting || confirmedDocs.length === 0 || !signature)
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                                }`}
                        >
                            {submitting ? 'Processing...' : 'Confirm Receipt'}
                        </button>
                    </form>
                </div>
            </div>
            <div className="mt-8 text-center text-gray-400 text-sm">
                &copy; 2026 Landlord Pro Compliance Systems
            </div>
        </div>
    );
}
